import { ReactNode } from 'react';
import { useDraggablePanel } from '@hooks/useDraggablePanel';

const STORAGE_KEY = 'flightdeck.callPanelPosition';

/** Clears the app header, whose own default height is the same 81px, so the panel does not
 * cover the account menu sitting in its top right corner. */
const HEADER_OFFSET = 81;
const EDGE_MARGIN = 16;

interface DraggableCallPanelProps {
  children: ReactNode;
}

/**
 * The floating panel an in-progress call lives in.
 *
 * It starts in the top right, below the header, and can be dragged anywhere from there. The
 * height is deliberately left to the content: the keypad and transfer control are taller than the
 * call notification on its own, and a fixed height used to push them off the bottom of the screen.
 */
export default function DraggableCallPanel({
  children
}: DraggableCallPanelProps) {
  const {
    panelRef,
    position,
    isDragging,
    handlePointerDown,
    handleKeyDown,
    resetPosition
  } = useDraggablePanel(STORAGE_KEY);

  // Anchored to the right edge until it is first dragged, so nothing needs measuring up front
  // and the default placement holds whatever the window width is.
  const positionStyle = position
    ? { left: position.x, top: position.y }
    : { right: EDGE_MARGIN, top: HEADER_OFFSET + EDGE_MARGIN };

  const grabCursor = isDragging ? 'cursor-grabbing' : 'cursor-grab';

  return (
    <div
      ref={panelRef}
      className="fixed z-50 w-[256px]"
      style={positionStyle}
      data-testid="call-panel"
      data-positioned={position ? 'custom' : 'default'}
    >
      <div
        className={`flex items-center justify-between rounded-t border border-b-0 border-gray-300 bg-gray-100 px-2 py-1 ${grabCursor}`}
        onPointerDown={handlePointerDown}
        data-testid="call-panel-drag-handle"
      >
        <button
          type="button"
          className={`-m-1 flex-1 p-1 text-left text-xs text-gray-500 ${grabCursor}`}
          onKeyDown={handleKeyDown}
          aria-label="Move the call panel. Use the arrow keys to reposition it."
          data-testid="call-panel-move"
        >
          <span aria-hidden="true">⠿</span>
        </button>

        <button
          type="button"
          className="rounded px-1 text-xs text-gray-500 hover:bg-gray-200 hover:text-gray-700"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={resetPosition}
          data-testid="call-panel-reset"
        >
          Reset
        </button>
      </div>

      <div className="rounded-b border border-t-0 border-gray-300 bg-white p-2">
        {children}
      </div>
    </div>
  );
}
